import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ChatWidget from './ChatWidget';

interface Rule {
  _id: string;
  name: string;
  prompt: string;
  description?: string;
}

interface FolderData {
  id: string;
  name: string;
  description?: string;
  fileCount: number;
}

interface LocationState {
  resultResponse?: any;
  apiResponse?: any;
  resultContent?: string;
  isNewSubmission?: boolean;
  complianceType?: string; // Tipo de compliance selecionado (copy, upload, form)
  selectedPrompts?: Rule[]; // Prompts selecionados no setup
  selectedFolders?: FolderData[]; // Pastas selecionadas no setup
  setupComplete?: boolean; // Indica se passou pelo setup
  complianceApiType?: string; // Tipo da API de compliance (upload_doc, upload_doc_and_text)
  apiError?: any; // Erro da API se houver
  directChat?: boolean; // Indica se deve abrir chat direto (vindo da sidebar)
  fromSidebar?: boolean; // Indica se veio da sidebar
}

const ChatWidgetContainer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isCompliancePending = location.pathname === '/compliance/pending';
  const state = location.state as LocationState;
  const [isNewSubmission, setIsNewSubmission] = useState(false);

  useEffect(() => {
    if (!isCompliancePending) return;

    const params = new URLSearchParams(location.search);
    const fullValues = params.getAll('full');
    const needsNormalize = fullValues.length !== 1 || fullValues[0] !== 'true';

    if (needsNormalize) {
      params.set('full', 'true');
      navigate(
        { pathname: location.pathname, search: `?${params.toString()}` },
        { replace: true, state: location.state }
      );
    }
  }, [isCompliancePending, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (isCompliancePending && state) {
      console.log('Estado disponível para o widget:', {
        resultResponse: state.resultResponse,
        apiResponse: state.apiResponse,
        resultContent: state.resultContent,
        isNewSubmission: state.isNewSubmission,
        complianceType: state.complianceType,
        selectedPrompts: state.selectedPrompts,
        selectedFolders: state.selectedFolders,
        setupComplete: state.setupComplete,
        complianceApiType: state.complianceApiType,
        apiError: state.apiError
      });

      // Verificar se é uma nova submissão, redirecionamento direto dos cards ou chat direto
      if (state.directChat || 
          state.isNewSubmission || 
          state.setupComplete || // Nova: reconhece quando passou pelo setup
          state.complianceType || // Novo: reconhece redirecionamento dos cards
          (state.resultResponse && !localStorage.getItem('lastResultResponse')) ||
          (state.apiResponse && !localStorage.getItem('lastApiResponse'))) {
        console.log('Detectada nova submissão, setup de compliance ou chat direto da sidebar');
        setIsNewSubmission(true);
        
        // Armazenar referências aos novos dados
        if (state.resultResponse) {
          localStorage.setItem('lastResultResponse', JSON.stringify({ timestamp: new Date().getTime() }));
        }
        if (state.apiResponse) {
          localStorage.setItem('lastApiResponse', JSON.stringify({ timestamp: new Date().getTime() }));
        }
        
        // Armazenar o tipo de compliance para referência
        if (state.complianceType) {
          localStorage.setItem('lastComplianceType', state.complianceType);
        }
        
        // Armazenar dados do setup para uso no widget
        if (state.selectedPrompts) {
          localStorage.setItem('selectedPrompts', JSON.stringify(state.selectedPrompts));
        }
        if (state.selectedFolders) {
          localStorage.setItem('selectedFolders', JSON.stringify(state.selectedFolders));
        }
        if (state.complianceApiType) {
          localStorage.setItem('complianceApiType', state.complianceApiType);
        }
        if (state.apiResponse) {
          localStorage.setItem('complianceApiResponse', JSON.stringify(state.apiResponse));
        }
      } else {
        setIsNewSubmission(false);
      }
    }
  }, [isCompliancePending, state]);

  // Só renderiza o ChatWidget se estiver na rota correta
  return isCompliancePending ? (
    <ChatWidget 
      isVisible={true} 
      complianceResult={state?.directChat ? {
        complianceType: 'menu',
        message: 'Chat iniciado diretamente',
        directChat: true
      } : (state?.resultResponse || state?.apiResponse || state?.resultContent || {
        complianceType: state?.complianceType,
        selectedPrompts: state?.selectedPrompts,
        selectedFolders: state?.selectedFolders,
        setupComplete: state?.setupComplete,
        complianceApiType: state?.complianceApiType,
        complianceApiResponse: state?.apiResponse,
        apiError: state?.apiError,
        message: `Iniciando conversa para ${
          state?.complianceType === 'copy' ? 'Copy & Paste' : 
          state?.complianceType === 'upload' ? 'Upload de Arquivo' : 
          state?.complianceType === 'form' ? 'Formulário' : 
          state?.complianceType === 'menu' ? 'Chat' : 'Compliance'
        }`
      })}
      newSubmission={isNewSubmission}
    />
  ) : null;
};

export default ChatWidgetContainer; 